/**
 * Kasus Indisipliner (dulu: Penanganan Indisipliner).
 * Plan: plan/PLAN_KASUS_INDISIPLINER.md · Panduan: docs/PANDUAN_KASUS_INDISIPLINER.md
 *
 * Tab "Belum Ditangani" = kasus terbuka di tabel kasus_indisipliner.
 * Tab "Sudah Ditangani" = riwayat tindakan di alert_penanganan (data lama + baru).
 * Sakit tidak termasuk indisipliner; santri sakit lama dipantau di Dasbor Klinik → Daftar Sakit Aktif.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    ArrowLeft, CheckCircle, Clock, FileText, X, Save, AlertTriangle, ShieldAlert, History, Calendar,
    GraduationCap, ChevronDown, ChevronUp, Send, Undo2, Ban, Lock, RefreshCw, Users, Filter, HeartHandshake, Camera, Image as ImageIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { useGuru } from '../../context/GuruContext';
import { useSantri } from '../../context/SantriContext';
import { useMasterData } from '../../context/MasterDataContext';
import { getJenjangFromClass, areJenjangsEqual } from '../../utils/jenjangUtils';
import { useGenderKelas } from '../../hooks/useGenderKelas';
import { useUserAccess } from '../../context/UserAccessContext';
import {
    useKasusIndisipliner, KasusIndisipliner, FilterKasus, akademikDari, rentangDariFilter,
    tindakKasus, batalkanKasus, delegasiHalaqoh, delegasiMurobbi, teruskanKasus, kembalikanKeWaliKelas,
    unggahBuktiFoto, MAKS_BUKTI_FOTO,
    PERAN_WAKA, PERAN_KADIV,
} from '../../hooks/useKasusIndisipliner';
import { KasusRekapSantri, BuktiFotoThumbs } from './KasusRekapSantri';
import { compressImage } from '../../utils/compressImage';

type ModeAksi = 'tindak' | 'halaqoh' | 'murobbi' | 'teruskan' | 'kembalikan' | 'batal';

const JUDUL_AKSI: Record<ModeAksi, string> = {
    tindak: 'Catat Tindakan',
    halaqoh: 'Delegasi ke Guru Halaqoh',
    murobbi: 'Minta Bantuan Murobbi',
    teruskan: 'Teruskan ke Pimpinan',
    kembalikan: 'Kembalikan ke Wali Kelas',
    batal: 'Batalkan Kasus',
};

const PILIHAN_TINDAKAN = [
    'Teguran Lisan', 'Pemanggilan Santri', 'Menghubungi Orang Tua', 'Surat Peringatan',
    'Dirujuk ke Kesiswaan / BK', 'Lainnya',
];

const bulanIni = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fmtTgl = (s?: string | null) =>
    s ? new Date(`${s.substring(0, 10)}T00:00:00`).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

/** "Senin, 3 Nov 2026 | Jam 1 - Fiqih (Ust. A)" → tanggal dan keterangan sesi. */
const pecahRincian = (ket: string): { tgl: string; sesi: string } => {
    const i = ket.indexOf(' | ');
    return i < 0 ? { tgl: ket, sesi: '' } : { tgl: ket.substring(0, i), sesi: ket.substring(i + 3) };
};

/** Daftar alpha per tanggal: tampil 3 baris pertama, sisanya lewat tombol. */
const RincianAlpha: React.FC<{
    rincian: { tanggal: string; keterangan: string }[];
    jumlah?: number;
    semua: boolean;
    onSemua?: () => void;
}> = ({ rincian, jumlah, semua, onSemua }) => {
    const tampil = semua || !onSemua ? rincian : rincian.slice(0, 3);
    // Kelompokkan per tanggal supaya beberapa jam di hari yang sama tidak mengulang tanggalnya.
    const grup: { tgl: string; sesi: string[] }[] = [];
    tampil.forEach(r => {
        const { tgl, sesi } = pecahRincian(r.keterangan);
        const g = grup.find(x => x.tgl === tgl);
        if (g) { if (sesi) g.sesi.push(sesi); } else grup.push({ tgl, sesi: sesi ? [sesi] : [] });
    });
    const terpotong = (jumlah || 0) > rincian.length;
    return (
        <div className="bg-gray-50/80 rounded-xl border border-gray-100 p-3">
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Calendar className="w-3.5 h-3.5 text-brand-primary" /> Waktu alpha
            </p>
            <ul className="space-y-2">
                {grup.map(g => (
                    <li key={g.tgl} className="text-xs">
                        <p className="font-bold text-gray-800">{g.tgl}</p>
                        {g.sesi.map((x, i) => (
                            <p key={i} className="text-gray-600 pl-3 border-l-2 border-red-200 ml-0.5 mt-0.5">{x}</p>
                        ))}
                    </li>
                ))}
            </ul>
            {onSemua && rincian.length > 3 && (
                <button onClick={onSemua} className="mt-2 text-xs font-bold text-brand-primary flex items-center gap-1">
                    {semua ? <><ChevronUp className="w-3.5 h-3.5" /> Ringkas</> : <><ChevronDown className="w-3.5 h-3.5" /> Lihat semua ({rincian.length})</>}
                </button>
            )}
            {terpotong && semua && (
                <p className="mt-1 text-[11px] text-gray-400">Ditampilkan {rincian.length} dari {jumlah} alpha.</p>
            )}
        </div>
    );
};

const labelStatus = (k: KasusIndisipliner): { teks: string; cls: string } => {
    if (k.status === 'halaqoh') return { teks: `Di guru halaqoh${k.pemegang_nama ? `: ${k.pemegang_nama}` : ''}`, cls: 'bg-sky-100 text-sky-700 border-sky-200' };
    if (k.status === 'murobbi') return { teks: `Di murobbi${k.pemegang_nama ? `: ${k.pemegang_nama}` : ''}`, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    if (k.status === 'diteruskan') return { teks: `Diteruskan ke ${k.pemegang_nama || k.pemegang_role || 'pimpinan'}`, cls: 'bg-violet-100 text-violet-700 border-violet-200' };
    return { teks: `Di wali kelas${k.wali_kelas_nama ? `: ${k.wali_kelas_nama}` : ''}`, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
};

export const PortalGuruPenangananIndisipliner: React.FC = () => {
    const navigate = useNavigate();
    const { authUser } = useAuth();
    const { checkIsKelasPutra, checkIsKelasPutri } = useGenderKelas();
    const { checkUserAccess, isSuperAdmin } = useUserAccess();
    const { santri } = useSantri();
    const { guruRecords } = useGuru();
    const { kelasRecords, murobbiRecords, halaqohRecords, tahunAjaranRecords, fetchKelasData, fetchMurobbiData, fetchTahunAjaranData } = useMasterData();

    useEffect(() => {
        fetchKelasData();
        fetchMurobbiData();
        fetchTahunAjaranData();
    }, [fetchKelasData, fetchMurobbiData, fetchTahunAjaranData]);

    const [activeTab, setActiveTab] = useState<'pending' | 'riwayat'>('pending');

    // Filter bersama kedua tab. Bulan default = bulan berjalan.
    const akSekarang = akademikDari();
    // Tahun ajaran & semester default = yang aktif (tahun ajaran dari Master Data bila ada).
    const [filterTahunAjaran, setFilterTahunAjaran] = useState(akSekarang.tahunAjaran);
    const [filterSemester, setFilterSemester] = useState(akSekarang.semester);
    const [taDisentuh, setTaDisentuh] = useState(false);
    const taAktif = tahunAjaranRecords.find(t => t.status === 'Aktif')?.nama;
    useEffect(() => {
        if (taAktif && !taDisentuh) setFilterTahunAjaran(taAktif);
    }, [taAktif, taDisentuh]);
    const [filterBulan, setFilterBulan] = useState(bulanIni());
    const [filterJenjang, setFilterJenjang] = useState('');
    const [filterKelas, setFilterKelas] = useState('');
    const [filterKategori, setFilterKategori] = useState('');

    const filter: FilterKasus = useMemo(
        () => ({ tahunAjaran: filterTahunAjaran, semester: filterSemester, bulan: filterBulan }),
        [filterTahunAjaran, filterSemester, filterBulan]
    );

    const { scope, kasus, ditindakPerSantri, pengaturan, loading: kasusLoading, error: kasusError, muat } = useKasusIndisipliner(filter);

    const isKadiv = checkUserAccess(authUser?.email || '', 'pg-kepala-divisi');
    const isTahfidz = checkUserAccess(authUser?.email || '', 'pg-tahfidz');
    const bolehFilterJenjang = isSuperAdmin || isKadiv || scope.isWaka;
    // Wali kelas (bukan admin/pimpinan) terkunci di kelasnya sendiri. Kasus yang
    // didelegasikan/diteruskan kepadanya dari kelas lain tetap tampil.
    const kelasTerkunci = !bolehFilterJenjang && scope.isWaliKelas;
    useEffect(() => {
        if (kelasTerkunci && scope.kelasWali.length === 1) setFilterKelas(scope.kelasWali[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kelasTerkunci, scope.kelasWali.join('|')]);
    const lolosKelas = (kelas: string, untukSaya: boolean) => {
        if (untukSaya) return true;
        if (kelasTerkunci && !scope.kelasWali.includes(kelas)) return false;
        return !filterKelas || kelas === filterKelas;
    };

    const myGuru: any = guruRecords.find(g => g.email === authUser?.email);
    const namaSaya = myGuru?.nama || authUser?.displayName || 'Guru';

    // ---------- Kasus terbuka ----------
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [aksi, setAksi] = useState<{ mode: ModeAksi; kasus: KasusIndisipliner } | null>(null);
    const [riwayatKasus, setRiwayatKasus] = useState<KasusIndisipliner | null>(null);
    const [tindakan, setTindakan] = useState(PILIHAN_TINDAKAN[0]);
    const [tindakanLain, setTindakanLain] = useState('');
    // Bukti foto (opsional): sudah dikompres di HP sebelum diunggah.
    const [buktiFoto, setBuktiFoto] = useState<{ file: File; preview: string }[]>([]);
    const [memprosesFoto, setMemprosesFoto] = useState(false);
    const kosongkanBukti = () => setBuktiFoto(prev => { prev.forEach(b => URL.revokeObjectURL(b.preview)); return []; });
    const tambahBukti = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        const sisa = MAKS_BUKTI_FOTO - buktiFoto.length;
        if (sisa <= 0) return;
        setMemprosesFoto(true);
        try {
            const hasil: { file: File; preview: string }[] = [];
            for (const f of Array.from(files).slice(0, sisa)) {
                if (!f.type.startsWith('image/')) continue;
                const kecil = await compressImage(f, 1024, 0.7);
                hasil.push({ file: kecil, preview: URL.createObjectURL(kecil) });
            }
            setBuktiFoto(prev => [...prev, ...hasil].slice(0, MAKS_BUKTI_FOTO));
            if (files.length > sisa) setErrorMessage(`Maksimal ${MAKS_BUKTI_FOTO} foto. Hanya ${sisa} foto pertama yang dipakai.`);
        } catch (e: any) {
            setErrorMessage(e?.message || 'Foto tidak bisa diproses.');
        } finally {
            setMemprosesFoto(false);
        }
    };
    const hapusBukti = (i: number) => setBuktiFoto(prev => {
        URL.revokeObjectURL(prev[i].preview);
        return prev.filter((_, j) => j !== i);
    });
    const [catatan, setCatatan] = useState('');
    const [teruskanKe, setTeruskanKe] = useState<typeof PERAN_WAKA | typeof PERAN_KADIV>(PERAN_WAKA);
    const [murobbiPilih, setMurobbiPilih] = useState('');
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const getKelas = (item: { kelas_nama?: string | null; santri_id: string }): string =>
        item.kelas_nama || santri.find(s => String(s.id) === String(item.santri_id))?.kelas || '';

    const batasTerlambat = Date.now() - pengaturan.batasTindakHari * 24 * 60 * 60 * 1000;
    const isTerlambat = (k: KasusIndisipliner) => new Date(k.created_at).getTime() < batasTerlambat;

    // Kasus bulan sebelumnya yang masih terbuka ikut dimuat (query bulan <= filter), tetapi
    // hanya ditampilkan bila pengguna menekan "Tampilkan", supaya daftar sesuai bulan terpilih.
    const [tampilBulanLama, setTampilBulanLama] = useState(false);
    useEffect(() => { setTampilBulanLama(false); }, [filterBulan]);
    const kasusLolos = kasus.filter(k => {
        const kelas = getKelas(k);
        if (filterJenjang && !areJenjangsEqual(k.jenjang || getJenjangFromClass(kelas), filterJenjang)) return false;
        if (!lolosKelas(kelas, k.status !== 'wali_kelas' && (k.pemegang_email || '').toLowerCase() === scope.email)) return false;
        if (filterKategori && k.kategori !== filterKategori) return false;
        return true;
    });
    const kasusBulanLama = filterBulan ? kasusLolos.filter(k => (k.bulan || '') < filterBulan) : [];
    const kasusTampil = filterBulan && !tampilBulanLama ? kasusLolos.filter(k => (k.bulan || '') >= filterBulan) : kasusLolos;
    // Kasus yang dipegang pengguna di atas, pantauan di bawah; di dalamnya urut tanggal alpha terbaru.
    kasusTampil.sort((a, b) => {
        const pa = scope.memegang(a) ? 0 : 1;
        const pb = scope.memegang(b) ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return b.periode_mulai.localeCompare(a.periode_mulai);
    });

    const ringkas = {
        total: kasusTampil.length,
        milik: kasusTampil.filter(scope.memegang).length,
        terlambat: kasusTampil.filter(isTerlambat).length,
        diteruskan: kasusTampil.filter(k => k.status !== 'wali_kelas').length,
    };

    const bukaAksi = (mode: ModeAksi, k: KasusIndisipliner) => {
        setAksi({ mode, kasus: k });
        setTindakan(PILIHAN_TINDAKAN[0]);
        setTindakanLain('');
        kosongkanBukti();
        setCatatan('');
        setTeruskanKe(PERAN_WAKA);
        setMurobbiPilih(scope.murobbiUntuk(k.santri_id).find(m => m.email)?.nama || '');
    };

    const simpanAksi = async () => {
        if (!aksi || !authUser?.email) return;
        const k = aksi.kasus;
        const oleh = { email: authUser.email.toLowerCase(), nama: namaSaya, peran: scope.peranSaya(k) };
        setSaving(true);
        try {
            if (aksi.mode === 'tindak') {
                const t = tindakan === 'Lainnya' ? (tindakanLain.trim() || 'Lainnya') : tindakan;
                const urls: string[] = [];
                for (const b of buktiFoto) urls.push(await unggahBuktiFoto(b.file, k.id));
                await tindakKasus(k, { tindakan: t, catatan, buktiFoto: urls }, oleh);
                kosongkanBukti();
                setSuccessMessage('Tindakan berhasil dicatat.');
            } else if (aksi.mode === 'halaqoh') {
                const guru = scope.guruHalaqohUntuk(k.santri_id);
                if (!guru) throw new Error('Santri ini belum terdaftar di halaqoh mana pun.');
                if (!guru.email) throw new Error(`Email guru halaqoh (${guru.nama}) belum terdata, jadi tugas tidak bisa dikirim.`);
                await delegasiHalaqoh(k, guru, catatan, oleh);
                setSuccessMessage(`Kasus didelegasikan ke ${guru.nama}.`);
            } else if (aksi.mode === 'murobbi') {
                const daftar = scope.murobbiUntuk(k.santri_id);
                const m = daftar.find(x => x.nama === murobbiPilih) || daftar[0];
                if (!m) throw new Error('Asrama santri ini belum punya murobbi di Master Data.');
                if (!m.email) throw new Error(`Email murobbi (${m.nama}) belum terdata, jadi tugas tidak bisa dikirim.`);
                await delegasiMurobbi(k, m, catatan, oleh);
                setSuccessMessage(`Kasus dikirim ke murobbi ${m.nama}.`);
            } else if (aksi.mode === 'teruskan') {
                await teruskanKasus(k, teruskanKe, catatan, oleh);
                setSuccessMessage(`Kasus diteruskan ke ${teruskanKe}.`);
            } else if (aksi.mode === 'kembalikan') {
                await kembalikanKeWaliKelas(k, catatan, oleh);
                setSuccessMessage('Kasus dikembalikan ke wali kelas.');
            } else if (aksi.mode === 'batal') {
                if (!catatan.trim()) throw new Error('Isi alasan pembatalan.');
                await batalkanKasus(k, catatan.trim(), oleh);
                setSuccessMessage('Kasus dibatalkan.');
            }
            setAksi(null);
            muat();
        } catch (e: any) {
            console.error('Gagal menyimpan aksi kasus:', e);
            setErrorMessage(e?.message || 'Gagal menyimpan. Silakan coba lagi.');
        } finally {
            setSaving(false);
        }
    };

    // ---------- Rekap manual ----------
    const [rekapOpen, setRekapOpen] = useState(false);
    const [rekapMulai, setRekapMulai] = useState('');
    const [rekapSelesai, setRekapSelesai] = useState('');
    const [rekapJalan, setRekapJalan] = useState(false);

    const jalankanRekap = async () => {
        if (!rekapMulai || !rekapSelesai) { setErrorMessage('Isi tanggal mulai dan selesai.'); return; }
        setRekapJalan(true);
        try {
            const fn = httpsCallable<{ mulai: string; selesai: string }, { kasusBaru: number; kasusDiperbarui: number }>(
                functions, 'rekapIndisiplinerManual', { timeout: 540000 }
            );
            const { data } = await fn({ mulai: rekapMulai, selesai: rekapSelesai });
            setRekapOpen(false);
            setSuccessMessage(`Rekap selesai: ${data.kasusBaru} kasus baru, ${data.kasusDiperbarui} kasus diperbarui.`);
            muat();
        } catch (e: any) {
            console.error('Rekap manual gagal:', e);
            setErrorMessage(e?.message || 'Rekap gagal.');
        } finally {
            setRekapJalan(false);
        }
    };

    // ---------- Riwayat (alert_penanganan) ----------
    const [riwayat, setRiwayat] = useState<any[]>([]);
    const [loadingRiwayat, setLoadingRiwayat] = useState(false);

    useEffect(() => {
        if (activeTab === 'riwayat') fetchRiwayat();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filterBulan, filterTahunAjaran, filterSemester]);

    // Filter tanggal dijalankan di query supaya data lama tidak terpotong limit.
    const applyFilterTanggal = (q: any) => {
        const r = rentangDariFilter(filter);
        if (!r) return q;
        return q.gte('tanggal_penanganan', r.mulai).lt('tanggal_penanganan', r.selesai);
    };

    const fetchRiwayat = async () => {
        setLoadingRiwayat(true);
        try {
            if (isSuperAdmin || scope.isWaka) {
                const { data, error } = await applyFilterTanggal(supabase.from('alert_penanganan').select('*').neq('status', 'DELEGATED').not('kategori', 'like', 'Sakit%'))
                    .order('created_at', { ascending: false })
                    .limit(500);
                if (error) throw error;
                setRiwayat(data || []);
                return;
            }

            let orQueryString = `ditangani_oleh_email.eq.${authUser?.email}`;
            const allowedSantriIds = new Set<string>();
            const userGuruName = myGuru?.nama || authUser?.displayName;
            const userDivisi = String(myGuru?.divisi || '').toLowerCase();
            const isMurobbi = checkUserAccess(authUser?.email || '', 'pg-murobbi');

            santri.forEach(s => {
                let include = false;
                const kelasInfo = kelasRecords.find(k => k.namaKelas === s.kelas);
                if (kelasInfo && kelasInfo.waliKelas === userGuruName) include = true;

                if (isMurobbi) {
                    const murobbiInfo = murobbiRecords.find(m => m.asramaBinaan?.includes(s.asrama));
                    if (murobbiInfo && murobbiInfo.nama === userGuruName) include = true;
                }

                if (isKadiv || isTahfidz) {
                    const sJenjang = getJenjangFromClass(s.kelas).toLowerCase();
                    if (userDivisi.includes('paud') && sJenjang === 'paud') include = true;
                    else if (userDivisi.includes('sd') && sJenjang === 'sd') include = true;
                    else if (userDivisi.includes('smp') && sJenjang === 'smp') include = true;
                    else if (userDivisi.includes('sma') && sJenjang === 'sma') include = true;
                    else if (userDivisi.includes('kesantrian putra') && (sJenjang === 'smp' || sJenjang === 'sma') && checkIsKelasPutra(s.kelas)) include = true;
                    else if (userDivisi.includes('kesantrian putri') && (sJenjang === 'smp' || sJenjang === 'sma') && checkIsKelasPutri(s.kelas)) include = true;
                    else if (isTahfidz && (halaqohRecords || []).some((h: any) =>
                        (h.pengampu === userGuruName || h.pengampuId === myGuru?.id) &&
                        (h.santri || []).some((hs: any) => String(hs.id) === String(s.id)))) include = true;
                }

                if (include) allowedSantriIds.add(String(s.id));
            });

            if (allowedSantriIds.size > 0 && allowedSantriIds.size < 500) {
                orQueryString += `,santri_id.in.(${Array.from(allowedSantriIds).join(',')})`;
            }

            const { data, error } = await applyFilterTanggal(supabase.from('alert_penanganan').select('*').neq('status', 'DELEGATED').not('kategori', 'like', 'Sakit%').or(orQueryString))
                .order('created_at', { ascending: false })
                .limit(500);
            if (error) throw error;
            setRiwayat(data || []);
        } catch (error) {
            console.error('Error fetching riwayat:', error);
        } finally {
            setLoadingRiwayat(false);
        }
    };

    const kategoriRiwayat = (r: any) => String(r.kategori || '').replace('_Pekanan', '');
    const filteredRiwayat = riwayat.filter(item => {
        const kelas = getKelas(item);
        if (filterJenjang && !areJenjangsEqual(getJenjangFromClass(kelas), filterJenjang)) return false;
        if (!lolosKelas(kelas, (item.ditangani_oleh_email || '').toLowerCase() === scope.email)) return false;
        if (filterKategori && kategoriRiwayat(item) !== filterKategori) return false;
        return true;
    });

    const sumberKelas = activeTab === 'pending' ? kasus : riwayat;
    const kelasOptions = kelasTerkunci ? [...scope.kelasWali].sort() : Array.from(new Set(
        sumberKelas
            .filter((r: any) => !filterJenjang || areJenjangsEqual(getJenjangFromClass(getKelas(r)), filterJenjang))
            .map((r: any) => getKelas(r))
    )).filter(Boolean).sort();

    const tahunAjaranOptions = Array.from(new Set([akSekarang.tahunAjaran, ...tahunAjaranRecords.map(t => t.nama)])).filter(Boolean).sort().reverse();

    const getSantriPhoto = (id: string) => {
        const s = santri.find(s => String(s.id) === String(id));
        return s?.foto || `https://ui-avatars.com/api/?name=${encodeURIComponent('Santri')}&background=0D8ABC&color=fff`;
    };

    const inputCls = 'w-full border border-gray-200 rounded-xl p-2.5 text-sm bg-white focus:ring-2 focus:ring-brand-primary/20';

    const panelFilter = (
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-4">
            <div className="flex items-center gap-2 mb-3 text-xs font-bold text-gray-700"><Filter className="w-4 h-4" /> Filter</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Tahun Ajaran</label>
                    <select value={filterTahunAjaran} onChange={e => { setTaDisentuh(true); setFilterTahunAjaran(e.target.value); }} className={inputCls}>
                        <option value="">Semua</option>
                        {tahunAjaranOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Semester</label>
                    <select value={filterSemester} onChange={e => setFilterSemester(e.target.value)} className={inputCls}>
                        <option value="">Semua</option>
                        <option value="Ganjil">Ganjil</option>
                        <option value="Genap">Genap</option>
                    </select>
                </div>
                <div className="min-w-0 col-span-2 md:col-span-1">
                    <label className="block text-xs font-bold text-gray-600 mb-1">Bulan</label>
                    <div className="flex gap-1 min-w-0">
                        <input type="month" value={filterBulan} onChange={e => setFilterBulan(e.target.value)} className={`${inputCls} min-w-0 flex-1`} />
                        {filterBulan && (
                            <button onClick={() => setFilterBulan('')} title="Semua bulan" aria-label="Semua bulan" className="w-10 shrink-0 flex items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
                {bolehFilterJenjang && (
                    <div>
                        <label className="block text-xs font-bold text-gray-600 mb-1">Jenjang</label>
                        <select value={filterJenjang} onChange={e => { setFilterJenjang(e.target.value); setFilterKelas(''); }} className={inputCls}>
                            <option value="">Semua Jenjang</option>
                            <option value="PAUD">PAUD</option>
                            <option value="SD">SD</option>
                            <option value="SMP">SMP</option>
                            <option value="SMA">SMA</option>
                        </select>
                    </div>
                )}
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Kelas</label>
                    <select
                        value={filterKelas}
                        onChange={e => setFilterKelas(e.target.value)}
                        disabled={kelasTerkunci && scope.kelasWali.length === 1}
                        className={`${inputCls} disabled:bg-gray-50 disabled:text-gray-600`}
                    >
                        <option value="">{kelasTerkunci ? 'Semua kelas saya' : 'Semua Kelas'}</option>
                        {kelasOptions.map(k => <option key={String(k)} value={String(k)}>{String(k)}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Kategori</label>
                    <select value={filterKategori} onChange={e => setFilterKategori(e.target.value)} className={inputCls}>
                        <option value="">Semua</option>
                        <option value="Kelas">Alpha Kelas</option>
                        <option value="Tahfidz">Alpha Tahfidz</option>
                    </select>
                </div>
            </div>
        </div>
    );

    const kartuKasus = (k: KasusIndisipliner) => {
        const memegang = scope.memegang(k);
        const ditindak = ditindakPerSantri[k.santri_id] || 0;
        const sering = ditindak >= pengaturan.batasTeruskan;
        const bolehTeruskan = isSuperAdmin || sering;
        const st = labelStatus(k);
        const rincian = k.rincian || [];
        const isWaliKelasKasus = k.status === 'wali_kelas';

        return (
            <div key={k.id} className={`bg-white p-5 rounded-2xl shadow-sm border flex flex-col gap-4 ${isTerlambat(k) ? 'border-red-200' : 'border-gray-100'}`}>
                <div className="flex items-start gap-4">
                    <img src={getSantriPhoto(k.santri_id)} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-gray-100 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-base truncate">{k.santri_nama}</h3>
                        <p className="text-xs text-gray-500 mb-2">{getKelas(k) || 'Tanpa kelas'} · {fmtTgl(k.periode_mulai)}{k.periode_selesai !== k.periode_mulai ? ` sampai ${fmtTgl(k.periode_selesai)}` : ''}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                            <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold flex items-center gap-1 border ${
                                k.kategori === 'Kelas' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-cyan-100 text-cyan-700 border-cyan-200'
                            }`}>
                                {k.kategori === 'Kelas' && <GraduationCap className="w-3 h-3" />}
                                {k.kategori === 'Tahfidz' && <FileText className="w-3 h-3" />}
                                Alpha {k.kategori}
                            </span>
                            <span className="text-[10px] px-2.5 py-0.5 rounded-full font-bold bg-slate-100 text-slate-600 border border-slate-200">
                                {k.jenis === 'pekanan' ? 'Pekanan' : 'Beruntun'}
                            </span>
                            <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold border ${st.cls}`}>{st.teks}</span>
                            {isTerlambat(k) && (
                                <span className="text-[10px] px-2.5 py-0.5 rounded-full font-bold bg-red-500 text-white">Terlambat</span>
                            )}
                            {sering && (
                                <span className="text-[10px] px-2.5 py-0.5 rounded-full font-bold bg-orange-100 text-orange-700 border border-orange-200">
                                    Sering ({ditindak}x ditindak)
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{k.ringkasan}</p>
                        {k.catatan_penerusan && (
                            <p className="text-xs text-gray-500 mt-2 italic">
                                {k.diteruskan_oleh_nama ? `${k.diteruskan_oleh_nama}: ` : ''}"{k.catatan_penerusan}"
                            </p>
                        )}
                    </div>
                </div>

                {rincian.length > 0 ? (
                    <RincianAlpha
                        rincian={rincian}
                        jumlah={k.jumlah}
                        semua={expandedId === k.id}
                        onSemua={() => setExpandedId(expandedId === k.id ? null : k.id)}
                    />
                ) : (
                    <p className="text-[11px] text-gray-400 italic">Rincian tanggal dan mapel tidak tersedia untuk kasus dari sistem lama.</p>
                )}

                {memegang ? (
                    <div className="space-y-2">
                        <div className="flex gap-2">
                            <button onClick={() => bukaAksi('tindak', k)} className="flex-1 min-w-0 h-11 bg-brand-primary text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2 hover:opacity-90">
                                <CheckCircle className="w-4 h-4 shrink-0" /> Tindak
                            </button>
                            <button onClick={() => setRiwayatKasus(k)} aria-label="Riwayat santri" title="Riwayat santri tahun ajaran ini" className="w-11 h-11 shrink-0 bg-white text-gray-600 border border-gray-200 rounded-xl flex items-center justify-center hover:bg-gray-50">
                                <History className="w-4 h-4" />
                            </button>
                            <button onClick={() => bukaAksi('batal', k)} aria-label="Batalkan kasus" title="Batalkan bila data presensi salah" className="w-11 h-11 shrink-0 bg-white text-gray-500 border border-gray-200 rounded-xl flex items-center justify-center hover:bg-gray-50">
                                <Ban className="w-4 h-4" />
                            </button>
                        </div>
                        {(() => {
                            const pilihan: { key: string; label: string; sub?: string; icon: React.ReactNode; cls: string; onClick: () => void; disabled?: boolean; title?: string }[] = [];
                            if (isWaliKelasKasus && k.kategori === 'Tahfidz') pilihan.push({
                                key: 'halaqoh', label: 'Guru Halaqoh', icon: <Users className="w-4 h-4" />,
                                cls: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100', onClick: () => bukaAksi('halaqoh', k),
                            });
                            if (isWaliKelasKasus) pilihan.push({
                                key: 'murobbi', label: 'Murobbi', icon: <HeartHandshake className="w-4 h-4" />,
                                cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100', onClick: () => bukaAksi('murobbi', k),
                            });
                            if (isWaliKelasKasus) pilihan.push({
                                key: 'teruskan', label: 'Teruskan', sub: bolehTeruskan ? undefined : `${ditindak}/${pengaturan.batasTeruskan}`,
                                icon: bolehTeruskan ? <Send className="w-4 h-4" /> : <Lock className="w-4 h-4" />,
                                cls: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100',
                                onClick: () => bolehTeruskan && bukaAksi('teruskan', k), disabled: !bolehTeruskan,
                                title: bolehTeruskan ? 'Teruskan ke Waka Kesiswaan atau Kepala Divisi' : `Bisa diteruskan setelah ${pengaturan.batasTeruskan} kasus santri ini ditindak di semester ini`,
                            });
                            if (k.status === 'halaqoh' || k.status === 'murobbi') pilihan.push({
                                key: 'kembalikan', label: 'Kembalikan ke Wali Kelas', icon: <Undo2 className="w-4 h-4" />,
                                cls: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100', onClick: () => bukaAksi('kembalikan', k),
                            });
                            if (pilihan.length === 0) return null;
                            const kolom = pilihan.length >= 3 ? 'grid-cols-3' : pilihan.length === 2 ? 'grid-cols-2' : 'grid-cols-1';
                            return (
                                <div>
                                    {isWaliKelasKasus && <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Serahkan ke</p>}
                                    <div className={`grid ${kolom} gap-2`}>
                                        {pilihan.map(o => (
                                            <button key={o.key} onClick={o.onClick} disabled={o.disabled} title={o.title}
                                                className={`min-w-0 px-2 border rounded-xl text-xs font-bold flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed ${pilihan.length >= 3 ? 'flex-col h-16 gap-1 text-center leading-tight' : 'h-11 gap-1.5'} ${o.cls}`}>
                                                <span className="shrink-0">{o.icon}</span>
                                                <span className={pilihan.length >= 3 ? '' : 'truncate'}>
                                                    {o.label}{o.sub && <span className="ml-1 text-[10px] font-semibold opacity-80">({o.sub})</span>}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                ) : (
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-gray-400 font-medium">Pantauan: kasus ini sedang dipegang {st.teks.charAt(0).toLowerCase() + st.teks.slice(1)}.</p>
                        <button onClick={() => setRiwayatKasus(k)} className="shrink-0 text-xs font-bold text-brand-primary flex items-center gap-1">
                            <History className="w-4 h-4" /> Riwayat
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const modalAksi = aksi && createPortal(
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[90] flex flex-col justify-end sm:justify-center sm:p-4">
            <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl sm:mx-auto flex flex-col max-h-[90vh] shadow-2xl">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="font-bold text-gray-900 text-lg">{JUDUL_AKSI[aksi.mode]}</h2>
                        <p className="text-xs text-gray-500 font-medium">{aksi.kasus.santri_nama} · {getKelas(aksi.kasus)}</p>
                    </div>
                    <button onClick={() => setAksi(null)} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-gray-500" /></button>
                </div>
                <div className="p-6 overflow-y-auto space-y-5">
                    <p className="text-xs text-slate-700 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-200/60">{aksi.kasus.ringkasan}</p>
                    {(aksi.kasus.rincian || []).length > 0 && (
                        <RincianAlpha rincian={aksi.kasus.rincian || []} jumlah={aksi.kasus.jumlah} semua />
                    )}

                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <KasusRekapSantri
                            santriId={aksi.kasus.santri_id}
                            tahunAjaran={aksi.kasus.tahun_ajaran || akSekarang.tahunAjaran}
                            kecualiId={aksi.kasus.id}
                            maksBaris={3}
                        />
                    </div>

                    {aksi.mode === 'tindak' && (
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">Bentuk Tindakan</label>
                            <select value={tindakan} onChange={e => setTindakan(e.target.value)} className="w-full border border-gray-200 rounded-xl p-3 text-sm">
                                {PILIHAN_TINDAKAN.map(t => <option key={t} value={t}>{t === 'Lainnya' ? 'Lainnya...' : t}</option>)}
                            </select>
                            {tindakan === 'Lainnya' && (
                                <input value={tindakanLain} onChange={e => setTindakanLain(e.target.value)} placeholder="Tulis bentuk tindakan" className="mt-2 w-full border border-gray-200 rounded-xl p-3 text-sm" />
                            )}

                            <label className="block text-xs font-bold text-gray-700 mt-4 mb-2 uppercase tracking-wider">
                                Bukti Foto <span className="normal-case font-medium text-gray-400">(opsional, maks {MAKS_BUKTI_FOTO})</span>
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {buktiFoto.map((b, i) => (
                                    <div key={b.preview} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                                        <img src={b.preview} alt={`Bukti ${i + 1}`} className="w-full h-full object-cover" />
                                        <button type="button" onClick={() => hapusBukti(i)} aria-label={`Hapus foto ${i + 1}`}
                                            className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center">
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                                {buktiFoto.length < MAKS_BUKTI_FOTO && (
                                    <div className="aspect-square grid grid-rows-2 gap-1.5">
                                        {[
                                            { kunci: 'kamera', label: 'Kamera', ikon: <Camera size={18} />, capture: true },
                                            { kunci: 'galeri', label: 'Galeri', ikon: <ImageIcon size={18} />, capture: false },
                                        ].map(o => (
                                            <label key={o.kunci} className={`rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center gap-1.5 text-gray-500 text-[11px] font-semibold ${memprosesFoto ? 'opacity-60 pointer-events-none' : 'cursor-pointer hover:border-teal-400 hover:text-teal-600'}`}>
                                                {memprosesFoto ? <RefreshCw size={16} className="animate-spin" /> : o.ikon}
                                                {memprosesFoto ? '...' : o.label}
                                                {/* Android: capture membuka kamera langsung; tanpa capture membuka galeri. */}
                                                <input type="file" accept="image/*" className="hidden" disabled={memprosesFoto}
                                                    {...(o.capture ? { capture: 'environment' as const } : { multiple: true })}
                                                    onChange={e => { tambahBukti(e.target.files); e.target.value = ''; }} />
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <p className="mt-2 text-[11px] text-gray-400">Pilih Kamera untuk memotret langsung, atau Galeri untuk foto yang sudah ada. Foto otomatis dikecilkan sebelum dikirim.</p>
                        </div>
                    )}

                    {aksi.mode === 'halaqoh' && (() => {
                        const g = scope.guruHalaqohUntuk(aksi.kasus.santri_id);
                        return (
                            <div className="bg-sky-50 p-4 rounded-xl border border-sky-100 text-xs text-sky-800 leading-relaxed">
                                {g ? <>Kasus akan dikirim ke guru halaqoh <b>{g.nama}</b>. Guru halaqoh bisa menindak atau mengembalikannya ke Anda.</>
                                    : 'Santri ini belum terdaftar di halaqoh mana pun.'}
                            </div>
                        );
                    })()}

                    {aksi.mode === 'murobbi' && (() => {
                        const daftar = scope.murobbiUntuk(aksi.kasus.santri_id);
                        return (
                            <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 text-xs text-emerald-800 leading-relaxed space-y-2">
                                {daftar.length === 0 ? (
                                    <p>Asrama santri ini belum punya murobbi di Master Data.</p>
                                ) : (
                                    <>
                                        {daftar.length > 1 ? (
                                            <select value={murobbiPilih} onChange={e => setMurobbiPilih(e.target.value)} className="w-full border border-emerald-200 rounded-xl p-2.5 text-sm bg-white text-gray-800">
                                                {daftar.map(m => (
                                                    <option key={m.nama} value={m.nama} disabled={!m.email}>
                                                        {m.nama}{m.mudabbir ? ' (Mudabbir)' : ''}{!m.email ? ' (email belum terdata)' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : null}
                                        <p>
                                            Kasus akan dikirim ke murobbi <b>{murobbiPilih || daftar[0].nama}</b>. Murobbi bisa menindak kasus alpha kelas maupun tahfidz, atau mengembalikannya ke Anda.
                                        </p>
                                    </>
                                )}
                            </div>
                        );
                    })()}

                    {aksi.mode === 'teruskan' && (
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">Teruskan ke</label>
                            <div className="flex gap-2">
                                {[PERAN_WAKA, PERAN_KADIV].map(p => (
                                    <button key={p} onClick={() => setTeruskanKe(p as any)}
                                        className={`flex-1 py-2.5 rounded-xl text-sm font-bold border ${teruskanKe === p ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200'}`}>
                                        {p}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                Santri ini sudah {ditindakPerSantri[aksi.kasus.santri_id] || 0} kali ditindak di semester ini.
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">
                            {aksi.mode === 'batal' ? 'Alasan Pembatalan (wajib)' : 'Catatan (opsional)'}
                        </label>
                        <textarea
                            value={catatan}
                            onChange={e => setCatatan(e.target.value)}
                            rows={4}
                            placeholder={aksi.mode === 'batal' ? 'Mis. santri sebenarnya hadir, presensi salah input.' : 'Ceritakan singkat prosesnya...'}
                            className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none"
                        />
                    </div>
                </div>
                <div className="p-6 pt-2 shrink-0">
                    <button onClick={simpanAksi} disabled={saving || memprosesFoto}
                        className={`w-full text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 ${aksi.mode === 'batal' ? 'bg-gray-700' : 'bg-brand-primary'}`}>
                        {saving ? <Clock className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                        {JUDUL_AKSI[aksi.mode]}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );

    const modalRiwayat = riwayatKasus && createPortal(
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[90] flex flex-col justify-end sm:justify-center sm:p-4">
            <div className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl sm:mx-auto flex flex-col max-h-[90vh] shadow-2xl">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="font-bold text-gray-900 text-lg">Riwayat Indisipliner</h2>
                        <p className="text-xs text-gray-500 font-medium">{riwayatKasus.santri_nama} · {getKelas(riwayatKasus)}</p>
                    </div>
                    <button onClick={() => setRiwayatKasus(null)} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-gray-500" /></button>
                </div>
                <div className="p-6 overflow-y-auto bg-gray-50/50">
                    <KasusRekapSantri santriId={riwayatKasus.santri_id} tahunAjaran={riwayatKasus.tahun_ajaran || akSekarang.tahunAjaran} />
                </div>
            </div>
        </div>,
        document.body
    );

    const modalRekap = rekapOpen && createPortal(
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[90] flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl p-6 space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="font-bold text-gray-900 text-lg">Rekap Manual</h2>
                    <button onClick={() => !rekapJalan && setRekapOpen(false)} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-gray-500" /></button>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                    Menghitung alpha kelas dan tahfidz per pekan di rentang ini (maksimal 31 hari), lalu memeriksa alpha beruntun hari ini. Kasus yang sudah ada tidak dibuat dobel.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-bold text-gray-600 mb-1">Mulai</label>
                        <input type="date" value={rekapMulai} onChange={e => setRekapMulai(e.target.value)} className={inputCls} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-600 mb-1">Selesai</label>
                        <input type="date" value={rekapSelesai} onChange={e => setRekapSelesai(e.target.value)} className={inputCls} />
                    </div>
                </div>
                <button onClick={jalankanRekap} disabled={rekapJalan}
                    className="w-full bg-brand-primary text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50">
                    {rekapJalan ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {rekapJalan ? 'Sedang merekap...' : 'Jalankan'}
                </button>
            </div>
        </div>,
        document.body
    );

    const modalPesan = (successMessage || errorMessage) && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center shadow-xl">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${successMessage ? 'bg-green-100' : 'bg-red-100'}`}>
                    {successMessage ? <CheckCircle className="w-8 h-8 text-green-600" /> : <AlertTriangle className="w-8 h-8 text-red-600" />}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{successMessage ? 'Berhasil!' : 'Terjadi Kesalahan'}</h3>
                <p className="text-gray-600 mb-6">{successMessage || errorMessage}</p>
                <button onClick={() => { setSuccessMessage(null); setErrorMessage(null); }}
                    className={`w-full text-white font-bold py-3 rounded-xl ${successMessage ? 'bg-brand-primary' : 'bg-red-600'}`}>
                    Tutup
                </button>
            </div>
        </div>,
        document.body
    );

    return (
        <div className="min-h-screen bg-gray-50/50 pb-20 font-sans">
            <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
                <div className="px-4 py-4 max-w-3xl mx-auto">
                    <div className="flex items-center gap-3 mb-4">
                        <button onClick={() => navigate('/portal-guru')} className="p-2 hover:bg-gray-100 rounded-full bg-gray-50 border border-gray-100">
                            <ArrowLeft className="w-5 h-5 text-gray-700" />
                        </button>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-xl font-bold text-gray-900 leading-tight tracking-tight">Kasus Indisipliner</h1>
                            <p className="text-sm text-gray-500 mt-0.5">Pencatatan & Tindak Lanjut Kedisiplinan Santri</p>
                        </div>
                        {scope.bolehRekapManual && (
                            <button onClick={() => setRekapOpen(true)} className="shrink-0 flex items-center gap-1.5 text-xs font-bold text-brand-primary bg-brand-primary/10 px-3 py-2 rounded-xl hover:bg-brand-primary/20">
                                <RefreshCw className="w-4 h-4" /> Rekap Manual
                            </button>
                        )}
                    </div>

                    <div className="flex bg-gray-100 p-1 rounded-xl">
                        <button
                            onClick={() => setActiveTab('pending')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-bold rounded-lg ${activeTab === 'pending' ? 'bg-white text-red-600 shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <ShieldAlert className="w-4 h-4" />
                            Belum Ditangani
                            {ringkas.milik > 0 && (
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${activeTab === 'pending' ? 'bg-red-100 text-red-600' : 'bg-red-500 text-white'}`}>{ringkas.milik}</span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('riwayat')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-bold rounded-lg ${activeTab === 'riwayat' ? 'bg-white text-brand-primary shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <History className="w-4 h-4" />
                            Sudah Ditangani
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-4 max-w-3xl mx-auto mt-2">
                {panelFilter}

                {activeTab === 'pending' ? (
                    kasusLoading ? (
                        <div className="flex flex-col items-center justify-center py-24">
                            <Clock className="w-10 h-10 text-brand-primary animate-spin mb-4" />
                            <p className="text-sm font-bold text-gray-700">Memuat kasus...</p>
                        </div>
                    ) : kasusError ? (
                        <div className="bg-red-50 border border-red-100 p-4 rounded-xl text-sm text-red-700">
                            Gagal memuat kasus: {kasusError}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-2">
                                <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
                                    <p className="text-2xl font-bold text-gray-900">{ringkas.milik}</p>
                                    <p className="text-[11px] text-gray-500 font-medium">Perlu Anda tindak</p>
                                </div>
                                <div className="bg-white rounded-xl border border-red-100 p-3 text-center">
                                    <p className="text-2xl font-bold text-red-600">{ringkas.terlambat}</p>
                                    <p className="text-[11px] text-gray-500 font-medium">Terlambat (&gt;{pengaturan.batasTindakHari} hari)</p>
                                </div>
                                <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
                                    <p className="text-2xl font-bold text-violet-600">{ringkas.diteruskan}</p>
                                    <p className="text-[11px] text-gray-500 font-medium">Didelegasikan / diteruskan</p>
                                </div>
                            </div>

                            {kasusBulanLama.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-center gap-3">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                                    <p className="flex-1 min-w-0 text-xs text-amber-900 leading-relaxed">
                                        {tampilBulanLama
                                            ? <>Menampilkan juga <b>{kasusBulanLama.length} kasus bulan sebelumnya</b> yang belum ditindak.</>
                                            : <>Ada <b>{kasusBulanLama.length} kasus bulan sebelumnya</b> yang belum ditindak.</>}
                                    </p>
                                    <button onClick={() => setTampilBulanLama(v => !v)} className="shrink-0 h-9 px-3 rounded-xl bg-white border border-amber-300 text-xs font-bold text-amber-800 hover:bg-amber-100">
                                        {tampilBulanLama ? 'Sembunyikan' : 'Tampilkan'}
                                    </button>
                                </div>
                            )}

                            {kasusTampil.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 px-6 text-center bg-white rounded-3xl border border-dashed border-gray-200/80">
                                    <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
                                    <h3 className="text-gray-900 font-bold text-lg mb-2">Tidak ada kasus terbuka</h3>
                                    <p className="text-sm text-gray-500 max-w-sm">
                                        Tidak ada kasus indisipliner yang belum ditangani untuk filter ini. Coba hapus filter bulan untuk melihat bulan lain.
                                    </p>
                                </div>
                            ) : kasusTampil.map(kartuKasus)}
                        </div>
                    )
                ) : (
                    <div className="space-y-4">
                        {loadingRiwayat ? (
                            <div className="flex justify-center py-20"><Clock className="w-8 h-8 text-brand-primary animate-spin" /></div>
                        ) : filteredRiwayat.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 px-6 text-center bg-white rounded-3xl border border-dashed border-gray-200/80">
                                <History className="w-10 h-10 text-gray-400 mb-4" />
                                <h3 className="text-gray-900 font-bold text-lg mb-2">Belum ada riwayat</h3>
                                <p className="text-sm text-gray-500">Tidak ada tindakan yang tercatat untuk filter ini.</p>
                            </div>
                        ) : filteredRiwayat.map((r, i) => (
                            <div key={r.id || i} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-start gap-4">
                                <img src={getSantriPhoto(r.santri_id)} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-gray-100 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start mb-1">
                                        <h3 className="font-bold text-gray-900 text-sm truncate pr-2">{r.santri_nama}</h3>
                                        {r.status === 'DIBATALKAN' ? (
                                            <span className="text-[10px] bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full font-bold shrink-0">Dibatalkan</span>
                                        ) : (
                                            <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-bold shrink-0">Selesai</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 flex flex-wrap items-center gap-2 mb-2">
                                        <FileText className="w-3.5 h-3.5 text-gray-400" />
                                        {getKelas(r) && <><span>{getKelas(r)}</span><span>•</span></>}
                                        <span className="font-medium text-gray-700">{kategoriRiwayat(r)} {String(r.kategori || '').includes('_Pekanan') ? '(Pekanan)' : ''}</span>
                                        <span>•</span>
                                        <span>{fmtTgl(r.tanggal_penanganan)}</span>
                                    </div>
                                    {r.detail_masalah && <p className="text-xs text-gray-600 mb-2">{r.detail_masalah}</p>}
                                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                        <p className="text-xs font-bold text-gray-700 mb-1">
                                            Tindakan: <span className="font-normal text-brand-primary">{r.tindakan}</span>
                                        </p>
                                        {r.catatan && <p className="text-[11px] text-gray-600 italic">"{r.catatan}"</p>}
                                        <BuktiFotoThumbs urls={r.bukti_foto} className="mt-2" />
                                        <p className="mt-2 pt-2 border-t border-gray-200/60 text-[10px] font-bold text-gray-500 flex items-center gap-1.5">
                                            <CheckCircle className="w-3 h-3 text-brand-primary" />
                                            Ditindak oleh: <span className="text-gray-700">{r.ditangani_oleh_nama || 'Sistem'}</span>
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setRiwayatKasus({
                                            ...r,
                                            tahun_ajaran: akademikDari(r.tanggal_penanganan ? new Date(`${String(r.tanggal_penanganan).substring(0, 10)}T00:00:00`) : new Date()).tahunAjaran,
                                        } as KasusIndisipliner)}
                                        className="mt-2 text-xs font-bold text-brand-primary flex items-center gap-1"
                                    >
                                        <History className="w-4 h-4" /> Riwayat santri
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {modalAksi}
            {modalRiwayat}
            {modalRekap}
            {modalPesan}
        </div>
    );
};
